import React, { useEffect, useState } from 'react';
import { Grid, Card, Button } from '@mui/material';
import AttachmentCard from '../../attachmentCard/AttachmentCard';
import LoadingBackdrop from 'components/LoadingBackdrop';
import { DeleteFile } from 'api/canguard/deleteFile';
import { useNotificationProvider } from 'hooks';
import CheckpointSignatureResponse from './CheckpointSignatureResponse';
import UppyDashboardModalGuard from '../../uppy/UppyDashboardModalGuard';

interface CheckpointAttachmentsProps {
	uppy: any;
	checkpointID: number;
	attachments: PatrolAttachment[];
	onUpdateAttachments: (attachments: PatrolAttachment[]) => void;
	isTypeSignature: boolean;
}

const CheckpointAttachmentResponse = (props: CheckpointAttachmentsProps) => {
	const {
		checkpointID,
		attachments,
		uppy,
		onUpdateAttachments,
		isTypeSignature,
	} = props;
	const { notifySuccess, notifyError } = useNotificationProvider();
	const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
	const [pendingDelete, setPendingDelete] = useState<boolean>(false);

	if (!uppy)
		throw new Error(
			'Uppy instance is required for CheckpointAttachmentResponse component',
		);

	const deleteAttachment = async (fileIDToDelete: number) => {
		setPendingDelete(true);
		try {
			await DeleteFile(fileIDToDelete);

			const updatedAttachments = attachments.filter(
				(item) => item.AttachmentID !== fileIDToDelete,
			);
			onUpdateAttachments(updatedAttachments);
			notifySuccess('File deleted successfully');
		} catch (e) {
			console.error('Error in file deletion: ', e);
			notifyError('Error deleting file');
		} finally {
			setPendingDelete(false);
		}
	};

	const handleFileUploadSuccess = (files: any) => {
		const newAttachments: PatrolAttachment[] = files.map((file: any) => {
			return {
				AttachmentID: file.response.body.VVFileID,
				Name: file.name,
				Size: file.size,
				ContentType: file.type,
			};
		});
		onUpdateAttachments([...attachments, ...newAttachments]);
	};

	const handleUploadModalOpen = () => {
		setUploadModalOpen(true);
	};
	const handleUploadModalClose = () => {
		setUploadModalOpen(false);
	};

	const saveSignatureCallback = () => {
		setUploadModalOpen(true);
	};

	uppy.on('complete', (result: any) => {
		handleFileUploadSuccess(result.successful);
	});

	return (
		<>
			<Grid container key={checkpointID} gap={2}>
				<Grid item xs={12}>
					{isTypeSignature ? (
						<CheckpointSignatureResponse
							uppy={uppy}
							saveSignatureCallback={saveSignatureCallback}
						/>
					) : (
						<Button
							variant="contained"
							onClick={handleUploadModalOpen}
						>
							Add Attachments
						</Button>
					)}
				</Grid>
				<Grid
					item
					xs={12}
					gap={1}
					display={'flex'}
					flexDirection={'column'}
				>
					{attachments.length > 0 &&
						attachments.map((file, index) => {
							return (
								<AttachmentCard
									key={file.AttachmentID}
									index={index}
									id={file.AttachmentID}
									name={file.Name}
									size={file.Size}
									onConfirmDelete={(id) =>
										deleteAttachment(id)
									}
									contentType={file.ContentType}
								/>
							);
						})}
				</Grid>
			</Grid>
			<UppyDashboardModalGuard
				uppy={uppy}
				openDashboard={uploadModalOpen}
				onCloseDashboard={handleUploadModalClose}
			/>
			<LoadingBackdrop open={pendingDelete} />
		</>
	);
};

export default CheckpointAttachmentResponse;
